import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Row, Col, Descriptions, Tag, Typography, Divider, Space, Button,
  Form, Select, DatePicker, Input, Checkbox, Radio, message, Alert, Modal, theme,
} from 'antd'
import {
  UserOutlined, CarOutlined, PhoneOutlined, MailOutlined,
  ExclamationCircleOutlined, UserAddOutlined, CalendarOutlined,
  SaveOutlined, LinkOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import type { WorkOrder } from '@/types'
import { useUpdateWorkOrder, useCreateWorkOrder } from '@/hooks/useWorkOrders'
import { useCustomers } from '@/hooks/useCustomers'
import { useCustomerVehicleHistory, useVehicles } from '@/hooks/useVehicles'
import { useQueryClient } from '@tanstack/react-query'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import { usePriorityTypes } from '@/hooks/useSystemTables'
import { useWorkOrderCarAssignment } from '@/hooks/useCourtesyCars'
import { usePermission } from '@/hooks/usePermission'
import { useInsuranceBranchTypes } from '@/hooks/useInsuranceBranchTypes'
import { CalendarModal } from '@/components/CalendarModal'
import CustomerFormModal from '@/components/CustomerFormModal'
import VehicleFormModal from '@/components/VehicleFormModal'
import VoiceTextarea from '@/components/VoiceTextarea'
import { PhotoCaptureManager, PhotoCaptureManagerHandle } from '@/components/PhotoCaptureManager'

const { Text, Paragraph } = Typography

const FREELY_EDITABLE_STATES = new Set(['bozza', 'annullata'])

const PRIORITA_COLORS: Record<string, string> = {
  bassa: 'default',
  media: 'blue',
  alta: 'orange',
  urgente: 'red',
}

interface FaseAccettazioneProps {
  workOrder: WorkOrder
  isEditable: boolean
  isNew?: boolean
  createStato?: string
  onSaved?: (patch: Partial<WorkOrder>) => void
  onCreated?: (wo: WorkOrder) => void
  onCancel?: () => void
  onDirtyChange?: (dirty: boolean) => void
}

// ─── READ-ONLY ───────────────────────────────────────────────────────────────

const FaseAccettazioneReadOnly: React.FC<{ workOrder: WorkOrder }> = ({ workOrder }) => (
  <div>
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={12}>
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Data Appuntamento">
            {workOrder.data_appuntamento
              ? dayjs(workOrder.data_appuntamento).format('DD/MM/YYYY HH:mm')
              : <Text type="secondary">—</Text>}
          </Descriptions.Item>
          {workOrder.data_fine_prevista && (
            <Descriptions.Item label="Fine Prevista">
              {dayjs(workOrder.data_fine_prevista).format('DD/MM/YYYY')}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Priorità">
            <Tag color={PRIORITA_COLORS[workOrder.priorita || 'media']}>
              {(workOrder.priorita || 'Media').charAt(0).toUpperCase() + (workOrder.priorita || 'media').slice(1)}
            </Tag>
          </Descriptions.Item>
          {workOrder.auto_cortesia_id && (
            <Descriptions.Item label="Auto Cortesia">
              <Tag color="green">ADC assegnata</Tag>
            </Descriptions.Item>
          )}
        </Descriptions>
      </Col>
    </Row>

    <Divider style={{ margin: '12px 0' }} />

    <Row gutter={16}>
      <Col xs={24} md={12}>
        <Space><UserOutlined style={{ color: '#1677ff' }} /><Text strong>Cliente</Text></Space>
        <div style={{ marginTop: 8, paddingLeft: 4 }}>
          <div><Text strong>{workOrder.customer_nome || workOrder.customer?.nome_completo || '—'}</Text></div>
          {workOrder.customer_telefono && (
            <div><PhoneOutlined style={{ marginRight: 4, color: '#8c8c8c' }} /><Text type="secondary">{workOrder.customer_telefono}</Text></div>
          )}
          {workOrder.customer_email && (
            <div><MailOutlined style={{ marginRight: 4, color: '#8c8c8c' }} /><Text type="secondary">{workOrder.customer_email}</Text></div>
          )}
        </div>
      </Col>
      <Col xs={24} md={12}>
        <Space><CarOutlined style={{ color: '#1677ff' }} /><Text strong>Veicolo</Text></Space>
        <div style={{ marginTop: 8, paddingLeft: 4 }}>
          <div><Text strong>{[workOrder.vehicle_marca, workOrder.vehicle_modello].filter(Boolean).join(' ') || '—'}</Text></div>
          {workOrder.vehicle_targa && (
            <div><Tag style={{ fontFamily: 'monospace', letterSpacing: 1 }}>{workOrder.vehicle_targa}</Tag></div>
          )}
          <div>
            {workOrder.vehicle_anno && <Text type="secondary">{workOrder.vehicle_anno}</Text>}
            {workOrder.vehicle_colore && <Text type="secondary"> · {workOrder.vehicle_colore}</Text>}
          </div>
        </div>
      </Col>
    </Row>

    {workOrder.valutazione_danno && (
      <>
        <Divider style={{ margin: '12px 0' }} />
        <Text strong>Descrizione Danno</Text>
        <Paragraph style={{ marginTop: 6, whiteSpace: 'pre-wrap' }} ellipsis={{ rows: 4, expandable: true, symbol: 'leggi di più' }}>
          {workOrder.valutazione_danno}
        </Paragraph>
      </>
    )}

    {workOrder.sinistro && (
      <>
        <Divider style={{ margin: '12px 0' }} />
        <Space><ExclamationCircleOutlined style={{ color: '#faad14' }} /><Text strong>Sinistro Assicurativo</Text></Space>
        <Descriptions column={2} size="small" style={{ marginTop: 8 }}>
          {workOrder.numero_sinistro && <Descriptions.Item label="N° Sinistro">{workOrder.numero_sinistro}</Descriptions.Item>}
          {workOrder.compagnia_sinistro && <Descriptions.Item label="Compagnia">{workOrder.compagnia_sinistro}</Descriptions.Item>}
          {workOrder.scoperto != null && <Descriptions.Item label="Scoperto">{workOrder.scoperto}%</Descriptions.Item>}
          {workOrder.franchigia != null && <Descriptions.Item label="Franchigia">€ {workOrder.franchigia}</Descriptions.Item>}
        </Descriptions>
      </>
    )}
  </div>
)

// ─── EDITABLE ────────────────────────────────────────────────────────────────

interface EditProps {
  workOrder: WorkOrder
  isNew?: boolean
  createStato?: string
  onSaved?: (patch: Partial<WorkOrder>) => void
  onCreated?: (wo: WorkOrder) => void
  onCancel?: () => void
  onDirtyChange?: (dirty: boolean) => void
}

const FaseAccettazioneEdit: React.FC<EditProps> = ({ workOrder, isNew, createStato, onSaved, onCreated, onCancel, onDirtyChange }) => {
  const { token } = theme.useToken()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const updateWO = useUpdateWorkOrder()
  const createWO = useCreateWorkOrder()
  const queryClient = useQueryClient()
  const photoCaptureRef = useRef<PhotoCaptureManagerHandle>(null)

  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(
    workOrder.customer_id > 0 ? workOrder.customer_id : undefined
  )
  const [showCalendar, setShowCalendar] = useState(false)
  const [showNewCustomer, setShowNewCustomer] = useState(false)
  const [showNewVehicle, setShowNewVehicle] = useState(false)
  const [showSinistro, setShowSinistro] = useState(workOrder.sinistro ?? false)
  const [appointmentDisplay, setAppointmentDisplay] = useState(
    workOrder.data_appuntamento ? dayjs(workOrder.data_appuntamento).format('DD/MM/YYYY HH:mm') : ''
  )
  const [appointmentDate, setAppointmentDate] = useState<string | undefined>(
    workOrder.data_appuntamento || undefined
  )

  const canViewADC = usePermission('courtesy_cars.view')

  const { data: customersData } = useCustomers(1, 500)
  const { data: customerVehicles, isLoading: isLoadingHistory } = useCustomerVehicleHistory(selectedCustomerId)
  const { data: allVehiclesData } = useVehicles(1, 500)
  const { data: adcAssignment } = useWorkOrderCarAssignment(workOrder.id > 0 ? workOrder.id : undefined)
  const { data: priorityTypes } = usePriorityTypes()
  const { data: insuranceBranches } = useInsuranceBranchTypes()

  useEffect(() => {
    form.setFieldsValue({
      customer_id: workOrder.customer_id > 0 ? workOrder.customer_id : undefined,
      vehicle_id: workOrder.vehicle_id > 0 ? workOrder.vehicle_id : undefined,
      priorita: workOrder.priorita || 'media',
      valutazione_danno: workOrder.valutazione_danno,
      sinistro: workOrder.sinistro,
      ramo_sinistro_id: workOrder.ramo_sinistro_id,
      numero_sinistro: workOrder.numero_sinistro,
      compagnia_sinistro: workOrder.compagnia_sinistro,
      compagnia_debitrice_sinistro: workOrder.compagnia_debitrice_sinistro,
      legale: workOrder.legale,
      autorita: workOrder.autorita,
      scoperto: workOrder.scoperto,
      franchigia: workOrder.franchigia,
      cessione_credito: workOrder.cessione_credito ?? null,
      data_fine_prevista: workOrder.data_fine_prevista ? dayjs(workOrder.data_fine_prevista) : undefined,
    })
  }, [workOrder.id])

  const handleCustomerChange = (customerId: number) => {
    const isLocked = !isNew && !FREELY_EDITABLE_STATES.has((workOrder.stato || '').toLowerCase())
    if (isLocked) {
      const prevCustomerId = workOrder.customer_id > 0 ? workOrder.customer_id : undefined
      Modal.confirm({
        title: 'Attenzione — modifica cliente',
        icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
        content: `La scheda è in stato "${workOrder.stato}". Stai modificando il cliente associato. Vuoi procedere?`,
        okText: 'Sì, modifica',
        cancelText: 'Annulla',
        onOk: () => {
          setSelectedCustomerId(customerId)
          form.setFieldValue('vehicle_id', undefined)
        },
        onCancel: () => {
          form.setFieldValue('customer_id', prevCustomerId)
        },
      })
    } else {
      setSelectedCustomerId(customerId)
      form.setFieldValue('vehicle_id', undefined)
    }
  }

  const handleVehicleChange = async (vehicleId: number) => {
    const prevVehicleId = isNew ? undefined : (workOrder.vehicle_id > 0 ? workOrder.vehicle_id : undefined)
    const isLocked = !isNew && !FREELY_EDITABLE_STATES.has((workOrder.stato || '').toLowerCase())

    try {
      const res = await axiosInstance.get(`${API_ENDPOINTS.VEHICLES}/${vehicleId}/last-work-order`)
      const lastWO = res.data as {
        work_order_id: number; numero_scheda: string; stato: string
        customer_id: number; customer_nome: string; is_open: boolean
      } | null

      // Hard block: veicolo con WO aperta (diversa dalla corrente)
      if (lastWO?.is_open && lastWO.work_order_id !== workOrder.id) {
        Modal.error({
          title: 'Veicolo non disponibile',
          content: 'Non è possibile aprire una scheda di lavoro per questo veicolo. Ha una scheda lavoro ancora aperta e il veicolo non è stato ancora riconsegnato al cliente.',
          onOk: () => form.setFieldValue('vehicle_id', prevVehicleId),
        })
        return
      }

      const differentCustomer = !!(lastWO && lastWO.work_order_id !== workOrder.id && lastWO.customer_id !== selectedCustomerId)

      let confirmTitle = ''
      let confirmContent = ''
      if (isLocked && differentCustomer) {
        confirmTitle = 'Attenzione — modifica veicolo'
        confirmContent = `La scheda è in stato "${workOrder.stato}". Stai modificando il veicolo e l'ultima scheda di questo veicolo (${lastWO!.numero_scheda}) era per ${lastWO!.customer_nome}. Vuoi procedere?`
      } else if (isLocked) {
        confirmTitle = 'Attenzione — modifica veicolo'
        confirmContent = `La scheda è in stato "${workOrder.stato}". Stai modificando il veicolo associato. Vuoi procedere?`
      } else if (differentCustomer) {
        confirmTitle = 'Veicolo già associato ad altro cliente'
        confirmContent = `L'ultima scheda di questo veicolo (${lastWO!.numero_scheda}) era per ${lastWO!.customer_nome}. Vuoi procedere?`
      }

      if (confirmContent) {
        Modal.confirm({
          title: confirmTitle,
          icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
          content: confirmContent,
          okText: 'Sì, procedi',
          cancelText: 'Annulla',
          onCancel: () => form.setFieldValue('vehicle_id', prevVehicleId),
        })
      }
    } catch {
      // Nessuna WO precedente — mostra solo warning stato se necessario
      if (isLocked) {
        Modal.confirm({
          title: 'Attenzione — modifica veicolo',
          icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
          content: `La scheda è in stato "${workOrder.stato}". Stai modificando il veicolo associato. Vuoi procedere?`,
          okText: 'Sì, procedi',
          cancelText: 'Annulla',
          onCancel: () => form.setFieldValue('vehicle_id', prevVehicleId),
        })
      }
    }
  }

  const buildPayload = (values: Record<string, unknown>) => {
    const payload: Record<string, unknown> = {
      customer_id: values.customer_id,
      vehicle_id: values.vehicle_id,
      priorita: values.priorita ?? 'media',
      valutazione_danno: values.valutazione_danno ?? null,
      sinistro: values.sinistro ?? false,
      ramo_sinistro_id: values.ramo_sinistro_id ?? null,
      numero_sinistro: values.numero_sinistro ?? null,
      compagnia_sinistro: values.compagnia_sinistro ?? null,
      compagnia_debitrice_sinistro: values.compagnia_debitrice_sinistro ?? null,
      legale: values.legale ?? null,
      autorita: values.autorita ?? null,
      scoperto: values.scoperto ? Number(values.scoperto) : null,
      franchigia: values.franchigia ? Number(values.franchigia) : null,
      cessione_credito: values.cessione_credito ?? null,
    }
    if (values.data_fine_prevista) {
      payload.data_fine_prevista = dayjs(values.data_fine_prevista as string).format('YYYY-MM-DD')
    }
    if (appointmentDate) {
      payload.data_appuntamento = appointmentDate
    }
    if (isNew && createStato) {
      payload.stato = createStato
    }
    return payload
  }

  const handleSave = () => {
    form.validateFields().then(values => {
      const payload = buildPayload(values)

      if (isNew) {
        createWO.mutate(payload as any, {
          onSuccess: (wo) => { onDirtyChange?.(false); onCreated?.(wo) },
          onError: (err: any) => {
            const detail = err?.response?.data?.detail
            if (err?.response?.status === 409 && detail) {
              Modal.error({ title: 'Veicolo non disponibile', content: detail })
            } else {
              message.error('Errore durante la creazione della scheda')
            }
          },
        })
      } else {
        updateWO.mutate(
          { id: workOrder.id, data: payload },
          {
            onSuccess: async (updated) => {
              try {
                await photoCaptureRef.current?.commitPhotos()
              } catch {
                return  // commitPhotos showed error; keep form open for retry
              }
              onDirtyChange?.(false)
              onSaved?.(updated as Partial<WorkOrder>)
            },
          }
        )
      }
    }).catch(() => {
      message.warning('Seleziona cliente e veicolo per procedere')
    })
  }

  const isPending = isNew ? createWO.isPending : updateWO.isPending

  return (
    <Form form={form} layout="vertical" size="small" onValuesChange={() => onDirtyChange?.(true)}>
      {/* Cliente + Veicolo */}
      <Row gutter={12}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Cliente"
            name="customer_id"
            rules={[{ required: true, message: 'Seleziona un cliente' }]}
          >
            <div style={{ display: 'flex', gap: 6 }}>
              <Form.Item name="customer_id" noStyle>
                <Select
                  showSearch
                  placeholder="Seleziona cliente"
                  optionFilterProp="label"
                  style={{ flex: 1 }}
                  onChange={handleCustomerChange}
                  options={customersData?.items.map(c => ({
                    value: c.id,
                    label: c.tipo === 'azienda' && c.ragione_sociale
                      ? c.ragione_sociale
                      : `${c.nome || ''} ${c.cognome || ''}`.trim(),
                  }))}
                />
              </Form.Item>
              <Button icon={<UserAddOutlined />} type="primary" onClick={() => setShowNewCustomer(true)} title="Nuovo cliente" />
            </div>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Veicolo"
            name="vehicle_id"
            rules={[{ required: true, message: 'Seleziona un veicolo' }]}
          >
            <div style={{ display: 'flex', gap: 6 }}>
              <Form.Item name="vehicle_id" noStyle>
                <Select
                  showSearch
                  placeholder={selectedCustomerId ? (isLoadingHistory ? 'Caricamento veicoli...' : 'Seleziona veicolo') : 'Prima il cliente'}
                  disabled={!selectedCustomerId || isLoadingHistory}
                  loading={isLoadingHistory}
                  optionFilterProp="label"
                  style={{ flex: 1 }}
                  onChange={handleVehicleChange}
                  options={(() => {
                    const historyIds = new Set((customerVehicles ?? []).map(v => v.id))
                    const historyOpts = (customerVehicles ?? []).map(v => ({
                      value: v.id,
                      label: `${v.targa} - ${v.marca || ''} ${v.modello || ''}`.trim(),
                    }))
                    const otherOpts = (allVehiclesData?.items ?? [])
                      .filter(v => !historyIds.has(v.id))
                      .map(v => ({
                        value: v.id,
                        label: `${v.targa} - ${v.marca || ''} ${v.modello || ''}`.trim(),
                      }))
                    return [
                      { label: 'Veicoli del cliente', options: historyOpts },
                      { label: 'Tutti i veicoli', options: otherOpts },
                    ]
                  })()}
                />
              </Form.Item>
              <Button
                icon={<CarOutlined />}
                type="primary"
                disabled={!selectedCustomerId}
                onClick={() => setShowNewVehicle(true)}
                title="Nuovo veicolo"
              />
            </div>
          </Form.Item>
        </Col>
      </Row>

      {/* Data appuntamento + consegna prevista + priorità */}
      <Row gutter={12}>
        <Col xs={24} sm={8}>
          <Form.Item label="Appuntamento">
            <Button
              icon={<CalendarOutlined />}
              onClick={() => setShowCalendar(true)}
              disabled={isNew}
              title={isNew ? 'Disponibile dopo la creazione' : appointmentDisplay || 'Imposta appuntamento'}
              style={{ width: '100%', textAlign: 'left', fontWeight: appointmentDisplay ? 500 : 400 }}
            >
              {appointmentDisplay || <span style={{ color: '#bfbfbf' }}>Nessun appuntamento</span>}
            </Button>
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item
            name="data_fine_prevista"
            label="Consegna Prevista"
            getValueProps={(v) => ({ value: v ? dayjs(v) : undefined })}
            normalize={(v) => v?.format ? v.format('YYYY-MM-DD') : v}
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="priorita" label="Priorità" initialValue="media">
            <Select
              options={priorityTypes?.filter(p => p.attivo).map(p => ({ value: p.nome, label: p.nome })) ?? [
                { value: 'bassa', label: 'Bassa' },
                { value: 'media', label: 'Media' },
                { value: 'alta', label: 'Alta' },
                { value: 'urgente', label: 'Urgente' },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Auto di Cortesia — solo visualizzazione, gestita dalla pagina Pianificazione */}
      {canViewADC && (
        <div style={{
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadiusLG,
          padding: '6px 12px',
          background: token.colorFillAlter,
          marginBottom: 16,
        }}>
          <div style={{ fontSize: token.fontSizeSM, color: token.colorTextLabel, fontWeight: 500, marginBottom: 4 }}>
            Auto di Cortesia
          </div>
          {adcAssignment ? (
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 10px' }}>
              <CarOutlined style={{ color: token.colorPrimary }} />
              <Text strong style={{ fontSize: 13 }}>{adcAssignment.courtesy_car_targa ?? `Auto #${adcAssignment.courtesy_car_id}`}</Text>
              {adcAssignment.courtesy_car_modello && (
                <Text type="secondary" style={{ fontSize: 12 }}>{adcAssignment.courtesy_car_modello}</Text>
              )}
              <Tag
                color={adcAssignment.stato === 'IN_CORSO' ? 'green' : adcAssignment.stato === 'PRENOTATA' ? 'blue' : 'default'}
                style={{ margin: 0, fontSize: 11 }}
              >
                {adcAssignment.stato === 'IN_CORSO' ? 'In corso' : adcAssignment.stato === 'PRENOTATA' ? 'Prenotata' : adcAssignment.stato}
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>·</Text>
              <Text style={{ fontSize: 12 }}>
                Dal <strong>{dayjs(adcAssignment.data_inizio).format('DD/MM/YYYY')}</strong>
                {' al '}
                <strong>{dayjs(adcAssignment.data_fine_prevista).format('DD/MM/YYYY')}</strong>
              </Text>
              <Button
                type="link"
                size="small"
                icon={<LinkOutlined />}
                onClick={() => navigate('/courtesy-cars/planning')}
                style={{ padding: 0, height: 'auto', fontSize: 12, marginLeft: 'auto' }}
              >
                Gestisci ADC
              </Button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Nessuna auto di cortesia assegnata</Text>
              <Button
                type="link"
                size="small"
                icon={<LinkOutlined />}
                onClick={() => navigate('/courtesy-cars/planning')}
                style={{ padding: 0, height: 'auto', fontSize: 12 }}
              >
                Vai alla pianificazione ADC
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Descrizione danno — riga intera */}
      <Row>
        <Col xs={24}>
          <Form.Item label="Descrizione Danno" name="valutazione_danno">
            <VoiceTextarea
              placeholder="Clicca il microfono e descrivi a voce il danno... oppure digita manualmente"
              rows={4}
              minHeight={110}
              maxHeight={300}
              classNamePrefix="descrizione-danno"
              label="Descrizione Danno"
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Sinistro */}
      <Form.Item name="sinistro" valuePropName="checked">
        <Checkbox onChange={(e) => setShowSinistro(e.target.checked)}>
          <Space><ExclamationCircleOutlined style={{ color: '#faad14' }} />Sinistro Assicurativo</Space>
        </Checkbox>
      </Form.Item>

      {showSinistro && (
        <div style={{ marginBottom: 12, padding: '10px 14px', background: token.colorWarningBg, borderRadius: 6, border: `1px solid ${token.colorWarningBorder}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            <Text strong style={{ fontSize: 13, color: token.colorWarningText }}>Dati Sinistro Assicurativo</Text>
          </div>
          <Row gutter={[12, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item name="ramo_sinistro_id" label="Ramo Sinistro">
                <Select
                  allowClear
                  placeholder="Seleziona ramo"
                  options={insuranceBranches?.map(b => ({ value: b.id, label: b.nome }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="numero_sinistro" label="N° Sinistro">
                <Input placeholder="es. SIN-2024-001" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="compagnia_sinistro" label="Compagnia Assicuratrice">
                <Input placeholder="Nome compagnia" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="compagnia_debitrice_sinistro" label="Compagnia Debitrice">
                <Input placeholder="Compagnia a carico" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item name="scoperto" label="Scoperto (%)">
                <Input type="number" min={0} max={100} placeholder="0" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item name="franchigia" label="Franchigia (€)">
                <Input type="number" min={0} placeholder="0" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="cessione_credito" label="Cessione di Credito">
                <Radio.Group>
                  <Radio value={true}>Sì</Radio>
                  <Radio value={false}>No</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="legale" label="Legale">
                <Input placeholder="Nome legale/studio" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="autorita" label="Autorità">
                <Input placeholder="es. Polizia, Carabinieri" />
              </Form.Item>
            </Col>
          </Row>
        </div>
      )}

      {/* Foto danno — solo su scheda esistente */}
      {!isNew && (
        <div style={{ padding: '10px 12px', background: token.colorFillAlter, borderRadius: 6, border: `1px solid ${token.colorBorderSecondary}`, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontWeight: 500 }}>Foto Danno</span>
            <Space>
              <Button size="small" type="primary" onClick={() => photoCaptureRef.current?.openWebcamModal()}>Scatta</Button>
              <Button size="small" onClick={() => photoCaptureRef.current?.openUploadModal()}>Carica</Button>
            </Space>
          </div>
          <PhotoCaptureManager
            ref={photoCaptureRef}
            workOrderId={workOrder.id}
            compact={true}
          />
        </div>
      )}

      {isNew && (
        <Alert
          type="info"
          showIcon
          message="Foto danno e collegamento Google Calendar disponibili dopo il salvataggio."
          style={{ marginBottom: 12 }}
        />
      )}

      {/* Salva */}
      <Space style={{ width: '100%' }}>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={isPending}
          onClick={handleSave}
        >
          {isNew ? 'Crea Scheda' : 'Salva Accettazione'}
        </Button>
        {!isNew && onCancel && (
          <Button onClick={() => {
            photoCaptureRef.current?.discardChanges()
            onCancel()
          }}>Annulla</Button>
        )}
      </Space>

      {/* Modali figlie */}
      <CustomerFormModal
        open={showNewCustomer}
        onCancel={() => setShowNewCustomer(false)}
        onSuccess={(customer) => {
          setShowNewCustomer(false)
          setSelectedCustomerId(customer.id)
          form.setFieldValue('customer_id', customer.id)
        }}
      />
      <VehicleFormModal
        open={showNewVehicle}
        onCancel={() => setShowNewVehicle(false)}
        onSuccess={(vehicle) => {
          setShowNewVehicle(false)
          // Aggiungi subito il veicolo alla cache per evitare che il Select mostri l'id grezzo
          queryClient.setQueryData(['customer-vehicle-history', selectedCustomerId], (old: any) =>
            old ? [...old, vehicle] : [vehicle]
          )
          form.setFieldValue('vehicle_id', vehicle.id)
        }}
      />
      {showCalendar && !isNew && (
        <CalendarModal
          onCancel={() => setShowCalendar(false)}
          onConfirm={(appt) => {
            const formatted = appt.appointment_date
              ? dayjs(appt.appointment_date).format('DD/MM/YYYY HH:mm')
              : ''
            setAppointmentDisplay(formatted)
            setAppointmentDate(appt.appointment_date ?? undefined)
            setShowCalendar(false)
            updateWO.mutate({ id: workOrder.id, data: { data_appuntamento: appt.appointment_date } })
          }}
          workOrderId={workOrder.id}
          currentAppointmentDate={workOrder.data_appuntamento}
          currentCustomerId={workOrder.customer_id ?? undefined}
          customerName={workOrder.customer_nome ?? ''}
        />
      )}
    </Form>
  )
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const FaseAccettazione: React.FC<FaseAccettazioneProps> = ({ workOrder, isEditable, isNew, createStato, onSaved, onCreated, onCancel, onDirtyChange }) => {
  if (isEditable) {
    return <FaseAccettazioneEdit workOrder={workOrder} isNew={isNew} createStato={createStato} onSaved={onSaved} onCreated={onCreated} onCancel={onCancel} onDirtyChange={onDirtyChange} />
  }
  return <FaseAccettazioneReadOnly workOrder={workOrder} />
}

export default FaseAccettazione
